import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import BigNumber from 'bignumber.js';
import './App.css';
import { CONFIG as ETHEREUM_CONFIG } from './configs/ethereum';
import { CONFIG as BITCOIN_CONFIG } from './configs/bitcoin';
import { CONFIG as SOLANA_CONFIG } from './configs/solana';
import { Config, NetworkInfo, UnitConfig } from './configs/types';

// Configure BigNumber
BigNumber.config({
  EXPONENTIAL_AT: 1e+9,
  DECIMAL_PLACES: 18
});

// Get configuration based on environment variable
const getConfig = (): Config => {
  switch (process.env.REACT_APP_CONFIG) {
    case 'bitcoin':
      return BITCOIN_CONFIG;
    case 'solana':
      return SOLANA_CONFIG;
    default:
      return ETHEREUM_CONFIG;
  }
};

const CONFIG = getConfig();
const STORAGE_KEY = `selectedNetwork_${process.env.REACT_APP_CONFIG || 'ethereum'}`;
const PRICE_CACHE_KEY = 'crypto_price_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// CryptoCompare symbol mapping for some networks that have different symbols
const CRYPTOCOMPARE_SYMBOL_MAP: Record<string, string> = {
  'binancecoin': 'BNB',
  'bitcoin-cash': 'BCH',
  'matic-network': 'MATIC',
  'avalanche-2': 'AVAX',
};

interface UnitValues {
  [key: string]: string;
}

interface PriceCacheEntry {
  price: number;
  timestamp: number;
  coingeckoId: string;
}

interface PriceCache {
  [key: string]: PriceCacheEntry;
}

const App: React.FC = () => {
  const [values, setValues] = useState<UnitValues>(() => {
    const initialValues: UnitValues = {};
    const mainUnit = Object.entries(CONFIG.units).find(([_, config]) => config.isMainUnit);
    if (mainUnit) {
      Object.keys(CONFIG.units).forEach(unit => {
        const fromFactor = CONFIG.units[mainUnit[0]].conversionFactor;
        const toFactor = CONFIG.units[unit].conversionFactor;
        initialValues[unit] = new BigNumber('1').times(fromFactor).dividedBy(toFactor).toString();
      });
    }
    return initialValues;
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [price, setPrice] = useState<number | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkInfo>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsedNetwork = JSON.parse(saved);
      // Verify the saved network exists in current configuration
      const validNetwork = CONFIG.networks.find(n => n.coingeckoId === parsedNetwork.coingeckoId);
      return validNetwork || CONFIG.defaultNetwork;
    }
    return CONFIG.defaultNetwork;
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getCryptoCompareSymbol = (network: NetworkInfo): string => {
    return CRYPTOCOMPARE_SYMBOL_MAP[network.coingeckoId] || network.symbol;
  };

  const getCachedPrice = (coingeckoId: string): PriceCacheEntry | null => {
    try {
      const cache = localStorage.getItem(PRICE_CACHE_KEY);
      if (cache) {
        const priceCache: PriceCache = JSON.parse(cache);
        const entry = priceCache[coingeckoId];
        if (entry && Date.now() - entry.timestamp < CACHE_DURATION) {
          return entry;
        }
      }
    } catch (error) {
      console.error('Error reading price cache:', error);
    }
    return null;
  };

  const setCachedPrice = (coingeckoId: string, price: number) => {
    try {
      const cache = localStorage.getItem(PRICE_CACHE_KEY);
      const priceCache: PriceCache = cache ? JSON.parse(cache) : {};
      priceCache[coingeckoId] = {
        price,
        timestamp: Date.now(),
        coingeckoId
      };
      localStorage.setItem(PRICE_CACHE_KEY, JSON.stringify(priceCache));
    } catch (error) {
      console.error('Error setting price cache:', error);
    }
  };

  const fetchCoinGeckoPrice = async (network: NetworkInfo): Promise<number | null> => {
    try {
      const response = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${network.coingeckoId}&vs_currencies=usd`);
      if (!response.ok) {
        throw new Error(`CoinGecko API error: ${response.status}`);
      }
      const data = await response.json();
      const price = data[network.coingeckoId]?.usd;
      if (price) {
        setCachedPrice(network.coingeckoId, price);
        return price;
      }
    } catch (error) {
      console.error('CoinGecko API error:', error);
    }
    return null;
  };

  const fetchCryptoComparePrice = async (network: NetworkInfo): Promise<number | null> => {
    try {
      const symbol = getCryptoCompareSymbol(network);
      const response = await fetch(`https://min-api.cryptocompare.com/data/price?fsym=${symbol}&tsyms=USD`);
      const data = await response.json();
      if (data.USD) {
        return data.USD;
      }
    } catch (error) {
      console.error('CryptoCompare API error:', error);
    }
    return null;
  };

  const fetchPrice = async () => {
    // First check cache
    const cachedEntry = getCachedPrice(selectedNetwork.coingeckoId);
    if (cachedEntry) {
      setPrice(cachedEntry.price);
      setLastUpdated(new Date(cachedEntry.timestamp).toLocaleTimeString());
      return;
    }

    // Try CoinGecko first
    const coingeckoPrice = await fetchCoinGeckoPrice(selectedNetwork);
    if (coingeckoPrice !== null) {
      setPrice(coingeckoPrice);
      setLastUpdated(new Date().toLocaleTimeString());
      return;
    }

    // Fallback to CryptoCompare
    const cryptoComparePrice = await fetchCryptoComparePrice(selectedNetwork);
    if (cryptoComparePrice !== null) {
      setPrice(cryptoComparePrice);
      setLastUpdated(new Date().toLocaleTimeString());
      return;
    }

    console.error('Failed to fetch price from both APIs');
  };

  const handleNetworkChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const network = CONFIG.networks.find(n => n.coingeckoId === event.target.value) || CONFIG.defaultNetwork;
    setSelectedNetwork(network);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(network));
  };

  const handleInputChange = (value: string, from: string) => {
    const newValues: UnitValues = {};
    const fromConfig = CONFIG.units[from];

    Object.entries(CONFIG.units).forEach(([unit, config]) => {
      if (unit === from) {
        newValues[unit] = value;
      } else {
        try {
          newValues[unit] = new BigNumber(value || '0')
            .times(fromConfig.conversionFactor)
            .dividedBy(config.conversionFactor)
            .toString();
        } catch {
          newValues[unit] = '0';
        }
      }
    });

    setValues(newValues);
  };

  useEffect(() => {
    // Find main unit and set initial values
    const mainUnit = Object.entries(CONFIG.units).find(([_, config]) => config.isMainUnit);
    if (mainUnit) {
      handleInputChange('1', mainUnit[0]);
    }
    fetchPrice();
    const interval = setInterval(fetchPrice, 60000);
    return () => clearInterval(interval);
  }, [selectedNetwork]);

  const calculateUsdValue = (amount: string): string => {
    if (!price || !amount) return '0.00';
    try {
      return new BigNumber(amount).times(price).toFixed(2);
    } catch {
      return '0.00';
    }
  };

  const isSolana = process.env.REACT_APP_CONFIG === 'solana';

  const renderInput = (unitKey: string, config: UnitConfig) => {
    // Get network-specific label override or use default label
    const label = config.isMainUnit ? 
      selectedNetwork.symbol : 
      (selectedNetwork.unitLabelOverrides?.[unitKey] || config.label);
    
    return (
      <div className="input-group" key={unitKey}>
        <label>{label}</label>
        <div className="input-with-copy">
          <input
            type="number"
            step="any"
            min="0"
            value={values[unitKey]}
            onChange={(e) => handleInputChange(e.target.value, unitKey)}
            placeholder="0"
          />
          <button onClick={() => copyToClipboard(values[unitKey])} className="copy-button">
            <FontAwesomeIcon icon={faCopy} />
          </button>
        </div>
        <div className="power-notation">
          {config.powerNotation}
        </div>
        {config.isMainUnit && price && (
          <div className="usd-value">
            ≈ ${calculateUsdValue(values[unitKey])} USD
          </div>
        )}
      </div>
    );
  };

  const defaultUnits = Object.entries(CONFIG.units).filter(([_, config]) => config.showByDefault);
  const advancedUnits = Object.entries(CONFIG.units).filter(([_, config]) => !config.showByDefault);

  return (
    <div className="converter-container">
      <div className="converter-form">
        {!isSolana && CONFIG.networks.length > 1 && (
          <div className="network-selector">
            <label>Network</label>
            <select 
              value={selectedNetwork.coingeckoId}
              onChange={handleNetworkChange}
            >
              {CONFIG.networks.map(network => (
                <option key={network.coingeckoId} value={network.coingeckoId}>
                  {network.name} ({network.symbol})
                </option>
              ))}
            </select>
          </div>
        )}

        {price && (
          <div className="price-banner">
            <div>1 {selectedNetwork.symbol} = ${price.toLocaleString()} USD</div>
            <div className="last-updated">Updated: {lastUpdated}</div>
          </div>
        )}
        
        {defaultUnits.map(([unit, config]) => renderInput(unit, config))}

        {!isSolana && advancedUnits.length > 0 && (
          <>
            <button 
              className="show-advanced-button"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 'Hide Advanced Units' : 'Show Advanced Units'}
            </button>

            {showAdvanced && (
              <>
                {advancedUnits.map(([unit, config]) => renderInput(unit, config))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default App;
